/**
 * Ticket conversation view (defect-first Phase 5, ported from mackey).
 *
 * The analyst has the written analysis on the Issue Analysis page; here they
 * interrogate it against the live code and schema. The backend answers with
 * the same source tools the written analysis used, and each answer carries
 * the code-recorded lookups behind it.
 *
 * The SQL Investigation panel docks underneath, full width, with a draggable
 * divider — a suggested ```sql block lands there as a ready-to-run card.
 */
import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ChevronDown, ChevronRight, Database, Loader2, Send, Terminal } from 'lucide-react';
import { MarkdownRenderer } from '../common/MarkdownRenderer';
import { useTicketChatStore } from '../../store/ticketChatStore';
import { SqlInvestigation, type SqlCard } from './SqlInvestigation';
import type { ExtractedEntity } from '../../services/issueAnalysisApi';

const SUGGESTIONS = [
  'Which table and column holds this value?',
  'Which procedure writes this table? Read its body.',
  'Where in the code is this field set?',
  'What did the analysis not verify?',
];

export function TicketChat() {
  const {
    ticketKey,
    modules,
    entities,
    turns,
    isLoading,
    error,
    showSqlDrawer,
    sqlPanelVh,
    ask,
    close,
    setSqlDrawer,
    setSqlPanelVh,
  } = useTicketChatStore();
  const [input, setInput] = useState('');
  // The seeded analysis starts collapsed: it is reference material, and a
  // long one filled the whole pane before the analyst could ask anything.
  const [openRefs, setOpenRefs] = useState<Record<number, boolean>>({});
  const [cards, setCards] = useState<SqlCard[]>([]);
  // Live height during a divider drag — committed to the (persisted) store
  // once on mouseup, never per-pixel.
  const [liveVh, setLiveVh] = useState<number | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startY: number; startVh: number } | null>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [turns.length, isLoading]);

  if (!ticketKey) return null;

  // A suggested query lands directly as a ready-to-run card — no store
  // handshake, so a click always works even with the drawer already open.
  const runSqlFromChat = (sql: string) => {
    setCards((prev) => [
      ...prev,
      {
        id: `chat-${Date.now()}`,
        label: 'From chat',
        sql,
        rows: [],
        rowCount: 0,
        kind: 'data',
        source: 'manual',
        edited: false,
        rationale: 'Suggested by the ticket conversation.',
      },
    ]);
    setSqlDrawer(true);
  };

  const goBack = () => {
    close();
    window.location.hash = '#/issue-analysis';
  };

  const conversationTurns = turns.filter((t) => !t.localOnly).length;

  const submit = (text: string) => {
    const q = text.trim();
    if (!q || isLoading) return;
    setInput('');
    void ask(q);
  };

  const clampVh = (vh: number) => Math.min(80, Math.max(20, vh));
  const onDividerMouseDown = (e: React.MouseEvent) => {
    dragRef.current = { startY: e.clientY, startVh: sqlPanelVh };
    const onMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      const deltaVh = ((dragRef.current.startY - ev.clientY) / window.innerHeight) * 100;
      setLiveVh(clampVh(dragRef.current.startVh + deltaVh));
    };
    const onUp = (ev: MouseEvent) => {
      if (dragRef.current) {
        const deltaVh = ((dragRef.current.startY - ev.clientY) / window.innerHeight) * 100;
        setSqlPanelVh(dragRef.current.startVh + deltaVh);
      }
      dragRef.current = null;
      setLiveVh(null);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <div
      className="h-full min-w-0 grid bg-white overflow-hidden"
      style={{
        gridTemplateRows: showSqlDrawer
          ? `auto minmax(0, 1fr) auto auto ${liveVh ?? sqlPanelVh}vh`
          : 'auto minmax(0, 1fr) auto',
      }}
    >
      {/* Header — the ticket this conversation is about */}
      <div className="flex items-center gap-3 px-5 py-3 border-b border-purple-200 bg-purple-50 flex-shrink-0">
        <button
          onClick={goBack}
          className="p-1.5 rounded hover:bg-purple-100 text-purple-700"
          title="Back to Issue Analysis"
        >
          <ArrowLeft size={16} />
        </button>
        <Database size={16} className="text-purple-700" />
        <div className="min-w-0">
          <div className="text-sm font-semibold text-purple-900">
            Chat about {ticketKey}
          </div>
          <div className="text-[11px] text-purple-700">
            Answers read the real code and schema before replying
          </div>
        </div>
        <button
          onClick={() => setSqlDrawer(!showSqlDrawer)}
          className={`ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
            showSqlDrawer
              ? 'bg-purple-700 text-white'
              : 'bg-white border border-purple-300 text-purple-800 hover:bg-purple-100'
          }`}
          title="Open the SQL Investigation panel"
        >
          <Terminal size={13} />
          SQL Investigation
        </button>
      </div>

      {/* Conversation */}
      <div className="min-h-0 overflow-y-auto px-5 py-4">
        <div className="max-w-3xl mx-auto space-y-5">
          {turns.map((t, i) =>
            t.role === 'user' ? (
              <div key={i} className="flex justify-end">
                <div className="max-w-[80%] px-4 py-2 rounded-lg bg-primary-600 text-white text-sm">
                  {t.content}
                </div>
              </div>
            ) : (
              <div key={i} className="text-sm">
                {t.localOnly ? (
                  <>
                    <button
                      onClick={() => setOpenRefs((o) => ({ ...o, [i]: !o[i] }))}
                      className="flex items-center gap-1.5 text-[11px] font-medium text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      {openRefs[i] ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      {t.label ?? 'Reference'}
                      <span className="text-gray-500">
                        {openRefs[i] ? '· hide' : '· read it'}
                      </span>
                    </button>
                    {openRefs[i] && (
                      <div className="mt-2">
                        {/* The seeded analysis carries the confirm-check
                            ```sql block — it gets the Run button too. */}
                        <MarkdownRenderer
                          content={t.content}
                          showTableOfContents={false}
                          onRunSql={runSqlFromChat}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <MarkdownRenderer
                    content={t.content}
                    showTableOfContents={false}
                    onRunSql={runSqlFromChat}
                  />
                )}
                {!t.localOnly && (
                  <div className="mt-2 flex items-center gap-3 text-[11px] text-gray-600">
                    {t.elapsedMs != null && <span>{(t.elapsedMs / 1000).toFixed(0)}s</span>}
                    {t.evidence && t.evidence.length > 0 && (
                      <span title={t.evidence.map((e) => e.tool).join(', ')}>
                        {t.evidence.length} source lookup{t.evidence.length === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ),
          )}

          {conversationTurns === 0 && (
            <div className="max-w-2xl mx-auto mt-8 text-center">
              <Database size={28} className="mx-auto text-purple-300 mb-3" />
              <h2 className="text-lg font-semibold text-gray-800">
                What would you like to know about {ticketKey}?
              </h2>
              <p className="text-sm text-gray-600 mt-2">
                Answers come from the real source code and schema — the model
                reads procedure and file bodies before replying.
              </p>
              <div className="flex flex-wrap gap-2 justify-center mt-5">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => submit(s)}
                    className="px-3 py-1.5 text-xs rounded-full border border-gray-300 text-gray-600 hover:border-purple-400 hover:text-purple-700 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Loader2 size={14} className="animate-spin" />
              Reading the code and schema…
            </div>
          )}

          {error && (
            <div className="px-3 py-2 rounded border border-red-200 bg-red-50 text-sm text-red-700">
              {error}
            </div>
          )}
          <div ref={endRef} />
        </div>
      </div>

      {/* Input */}
      <div className="border-t border-gray-200 px-5 py-3 flex-shrink-0">
        <div className="max-w-3xl mx-auto flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit(input);
              }
            }}
            rows={1}
            placeholder={`Ask about ${ticketKey}…`}
            className="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            disabled={isLoading}
          />
          <button
            onClick={() => submit(input)}
            disabled={isLoading || !input.trim()}
            className="p-2 rounded-lg bg-primary-600 text-white disabled:opacity-40 hover:bg-primary-500 transition-colors"
            title="Send"
          >
            <Send size={16} />
          </button>
        </div>
      </div>

      {/* Docked SQL Investigation — full width beats side-by-side: a
          generated SELECT with a JOIN needs the window, and the result
          grid needs it more. */}
      {showSqlDrawer && (
        <>
          <div
            onMouseDown={onDividerMouseDown}
            className="h-1.5 cursor-row-resize bg-gray-200 hover:bg-purple-300 transition-colors"
            title="Drag to resize"
          />
          <div className="min-h-0 overflow-y-auto border-t border-gray-200 px-4 py-3">
            <SqlInvestigation
              ticketKey={ticketKey}
              modules={modules}
              entities={entities as ExtractedEntity[]}
              cards={cards}
              setCards={setCards}
              title="SQL Investigation"
              subtitle="Read-only query engine — checks the data behind the conversation."
              defaultExpanded
            />
          </div>
        </>
      )}
    </div>
  );
}

export default TicketChat;
